import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { Trans } from "react-i18next";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Lock, Star } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { LoadingState } from "../../../components/ui";
import {
  fetchPollById,
  submitVote,
  effectiveStatus,
  hasVoted,
  getMyVote,
  isInAudience,
  canViewResults,
} from "../../../services/pollServices";
import { StatusPill } from "../components/StatusPill";
import { Toast } from "../components/Toast";
import { useOnline } from "../hooks/useOnline";
import { cn } from "../../../lib/utils";
import {
  ROUTES,
  RATING_SCALE,
  formatDateTime,
  timeRemaining,
} from "../constants/pollOptions";

/** Renders the response control matched to the poll's Response Type. */
function ResponseControl({ poll, value, onChange, readOnly }) {
  if (poll.responseType === "Single Choice" || poll.responseType === "Multiple Choice") {
    const multiple = poll.responseType === "Multiple Choice";
    const selected = value.optionIds ?? [];

    const toggle = (optionId) => {
      if (readOnly) return;
      if (multiple) {
        onChange({
          optionIds: selected.includes(optionId)
            ? selected.filter((id) => id !== optionId)
            : [...selected, optionId],
        });
      } else {
        onChange({ optionIds: [optionId] });
      }
    };

    return (
      <div className="space-y-2.5">
        {poll.options.map((option) => {
          const active = selected.includes(option.optionId);
          return (
            <label
              key={option.optionId}
              className={cn(
                "flex items-center gap-3 rounded-xl border px-4 py-3 min-h-[48px] transition",
                readOnly ? "cursor-default" : "cursor-pointer",
                active
                  ? "border-accent bg-accent/5"
                  : "border-(--color-text-border) bg-white hover:bg-workspace/50"
              )}>

              <input
                type={multiple ? "checkbox" : "radio"}
                name="poll-response"
                className="h-4 w-4 shrink-0"
                checked={active}
                disabled={readOnly}
                onChange={() => toggle(option.optionId)} />

              <span className="text-sm text-foreground wrap-break-word">{option.text}</span>
            </label>
          );
        })}
        {multiple && !readOnly && (
          <p className="text-[12px] per-file-hint">
            <Trans i18nKey="polls.pickOneOrMoreOptions" />
          </p>
        )}
      </div>
    );
  }

  if (poll.responseType === "Rating") {
    return (
      <div>
        <div className="flex gap-2 flex-wrap">
          {RATING_SCALE.map((n) => {
            const active = value.rating === n;
            return (
              <button
                key={n}
                type="button"
                disabled={readOnly}
                onClick={() => onChange({ rating: n })}
                className={cn(
                  "h-[56px] w-[56px] rounded-xl border text-lg font-bold transition inline-flex flex-col items-center justify-center gap-0.5",
                  active
                    ? "border-accent bg-accent/5 text-accent"
                    : "border-(--color-text-border) bg-white text-foreground hover:bg-workspace/50",
                  readOnly && "cursor-default"
                )}>

                <Star className={cn("h-3.5 w-3.5", active && "fill-current")} />
                {n}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[12px] per-file-hint">
          <Trans i18nKey="polls.1Poor5Excellent" />
        </p>
      </div>
    );
  }

  return null;
}

/**
 * Screen 8 — Vote Poll.
 * One response per employee, final on submit. Audience membership, the
 * voting window and the duplicate check are all re-validated on submit, so a
 * stale tab can't slip a second vote through.
 */
export function VotePollPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const online = useOnline();

  const [poll, setPoll] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [value, setValue] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [justVoted, setJustVoted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const found = await fetchPollById(id);
        if (cancelled) return;
        setPoll(found);

        // Only fetch the answer back when the server says this caller voted —
        // a voter may always see their own response, even on an anonymous poll.
        if (hasVoted(found)) {
          const mine = await getMyVote(id).catch(() => null);
          if (!cancelled) setMyVote(mine);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || "Poll not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const breadcrumb = [
    { label: "Home", to: "/" },
    { label: "Polls", to: ROUTES.essDashboard },
    { label: "Vote" },
  ];

  if (loading) return <AppLayout breadcrumb={breadcrumb}><LoadingState /></AppLayout>;

  if (loadError || !poll) {
    return (
      <AppLayout breadcrumb={breadcrumb}>
        <PageTitleRow className="mb-5" back={<PageBackLink to={ROUTES.essDashboard} />}><h1><Trans i18nKey="polls.vote">Vote on poll</Trans></h1></PageTitleRow>
        <p className="p-6 text-sm text-error">{loadError ?? "Poll not found."}</p>
      </AppLayout>
    );
  }

  const status = effectiveStatus(poll);
  const alreadyVoted = hasVoted(poll);
  // The dashboard only lists polls this employee is targeted by, and the vote
  // endpoint re-checks membership, so reaching here off-audience means the
  // poll was opened by direct URL.
  const inAudience = isInAudience(poll);

  if (!inAudience) {
    return (
      <AppLayout breadcrumb={breadcrumb}>
        <PageTitleRow className="mb-5" back={<PageBackLink to={ROUTES.essDashboard} />}><h1><Trans i18nKey="polls.vote">Vote on poll</Trans></h1></PageTitleRow>
        <div className="max-w-[640px] rounded-2xl bg-white border border-(--color-text-border)/70 p-8 text-center">
          <Lock className="h-6 w-6 mx-auto mb-3 per-file-hint" />
          <p className="text-sm font-semibold text-foreground">
            <Trans i18nKey="polls.thisPollIsnTOpenToYou" />
          </p>
          <p className="mt-1 text-sm per-file-hint">
            <Trans i18nKey="polls.youReNotInThisPollSAudience" />
          </p>
        </div>
      </AppLayout>
    );
  }

  const readOnly = alreadyVoted || status !== "Active";
  const displayValue =
    alreadyVoted && myVote
      ? { optionIds: myVote.optionIds ?? [], rating: myVote.rating }
      : value;

  const isAnswered =
    (poll.responseType === "Single Choice" && (value.optionIds ?? []).length === 1) ||
    (poll.responseType === "Multiple Choice" && (value.optionIds ?? []).length >= 1) ||
    (poll.responseType === "Rating" && Boolean(value.rating));

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const response =
        poll.responseType === "Rating"
          ? { rating: value.rating }
          : { optionIds: value.optionIds };

      // The POST can come back with an empty body (depending on the
      // gateway/CORS layer), so the screen is deliberately NOT built from its
      // payload — an empty one is what produced the earlier blank "voted"
      // page. Confirm and reload authoritative state instead.
      await submitVote(poll.pollId, response);

      // Confirm the vote actually landed by reading it back from the same ESS
      // service that just wrote it. If it isn't there, the write silently
      // failed — surface that rather than pretending the vote succeeded.
      const mine = await getMyVote(poll.pollId).catch(() => null);
      if (!mine) {
        setSubmitError(
          "Your vote couldn't be recorded. Please try again — if this keeps happening, contact your administrator."
        );
        return;
      }
      setMyVote(mine);

      // Reload the poll so the results-visibility flags reflect "has now
      // voted". Fall back to the poll already in state if the reload fails, so
      // the screen is never blank.
      const refreshed = await fetchPollById(poll.pollId).catch(() => null);
      const freshPoll = refreshed?.pollId ? refreshed : poll;
      setPoll(freshPoll);

      // Keep them on this confirmation/results screen only when the poll lets
      // this voter see results after voting. Otherwise the poll is finished for
      // them, so return to the dashboard, where it now shows under Completed.
      if (canViewResults(freshPoll)) {
        setJustVoted(true);
      } else {
        navigate(ROUTES.essDashboard, {
          state: { tab: "completed", toast: "Vote submitted successfully" },
        });
      }
    } catch (err) {
      setSubmitError(err.message || "Could not submit your vote. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resultsOpen = canViewResults(poll);

  return (
    <AppLayout breadcrumb={breadcrumb}>

      <div className="max-w-[680px] bg-white rounded-2xl border border-(--color-text-border)/70 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] overflow-hidden">
        <div className="px-6 sm:px-8 py-6 border-b border-(--color-text-border)/70">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <StatusPill status={alreadyVoted ? "Completed" : status} />
            {status === "Active" && (
              <span className="text-[12px] font-semibold per-file-hint">
                {timeRemaining(poll.endDateTime)}
              </span>
            )}
            {poll.anonymous && (
              <span className="text-[12px] font-semibold text-accent">
                <Trans i18nKey="polls.anonymousPoll" />
              </span>
            )}
          </div>

          <PageTitleRow back={<PageBackLink to={ROUTES.essDashboard} />}>
            <h1 className="text-[22px] font-bold text-foreground wrap-break-word">{poll.pollQuestion}</h1>
          </PageTitleRow>
          <p className="mt-1.5 text-[13px] per-file-hint">{poll.pollTitle}</p>

          {poll.description && (
            <p className="mt-4 text-sm leading-relaxed text-foreground whitespace-pre-line wrap-break-word">
              {poll.description}
            </p>
          )}
        </div>

        <div className="px-6 sm:px-8 py-6">
          {alreadyVoted && (
            <div className="mb-5 flex items-start gap-2 rounded-xl bg-(--status-published-bg) px-4 py-3 text-[13px] font-semibold text-(--status-published-fg)">
              <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {myVote?.votedOn
                  ? `Your vote was recorded on ${formatDateTime(myVote.votedOn)}.`
                  : "Your vote was recorded."}{" "}
                <Trans i18nKey="validation.aSubmittedResponseCanTBeChangedOr" />
              </span>
            </div>
          )}

          {!alreadyVoted && status === "Closed" && (
            <div className="mb-5 rounded-xl bg-(--status-archived-bg) px-4 py-3 text-[13px] font-semibold text-(--status-archived-fg)">
              <Trans i18nKey="polls.thisPollClosedOn" /> {formatDateTime(poll.endDateTime)}{" "}
              <Trans i18nKey="polls.votingHasEnded" />
            </div>
          )}

          <ResponseControl
            poll={poll}
            value={displayValue}
            onChange={(patch) => setValue((prev) => ({ ...prev, ...patch }))}
            readOnly={readOnly} />
        </div>

        {submitError && (
          <div className="px-6 sm:px-8 py-3 bg-error/10 border-t border-error/25 text-[13px] font-semibold text-error">
            ⚠ {submitError}
          </div>
        )}

        {!online && !alreadyVoted && status === "Active" && (
          <div className="px-6 sm:px-8 py-3 bg-(--status-warning-bg) text-[13px] font-semibold text-(--status-warning-fg)">
            <Trans i18nKey="polls.youReOfflineConnectToSubmitYourVote" />
          </div>
        )}

        <div className="flex justify-end gap-3 px-6 sm:px-8 py-5 bg-[#f8fafc] border-t border-(--color-text-border)/70">
          {readOnly ? (
            <>
              <button
                type="button"
                onClick={() => navigate(ROUTES.essDashboard)}
                className="min-h-[48px] h-11 px-6 rounded-xl border border-(--color-text-border) bg-white text-sm font-semibold text-foreground hover:bg-workspace">
                <Trans i18nKey="formActions.backToPolls" />
              </button>
              {resultsOpen && (
                <button
                  type="button"
                  onClick={() => navigate(ROUTES.essResults(poll.pollId))}
                  className="min-h-[48px] h-11 px-6 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-accent">
                  <Trans i18nKey="formActions.viewResults" />
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isAnswered || !online || submitting}
              className="min-h-[48px] h-11 px-7 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? "Submitting…" : "Submit Vote"}
            </button>
          )}
        </div>
      </div>

      {justVoted && (
        <Toast tone="success" onDone={() => setJustVoted(false)} duration={4000}>
          <Trans i18nKey="formActions.voteSubmittedSuccessfully" />
        </Toast>
      )}
    </AppLayout>
  );
}
